/**
 * 登录用户状态管理
 */
import { defineStore } from 'pinia';
import type { BadgeProps } from 'element-plus';
import { toTree, mapTree, isExternalLink, EleMessage } from 'ele-admin-plus/es';
import type { MenuItem } from 'ele-admin-plus/es/ele-pro-layout/types';
import type { User } from '@/api/system/user/model';
import type { Menu, TenantMenu } from '@/api/system/menu/model';
import type { DictionaryData } from '@/api/system/dictionary-data/model';
import { getUserInfo } from '@/api/layout';
import type { TenantRole } from '@/api/system/role/model';
import type { TenantDataScope } from '@/api/bsl/tenant/data/scope/model';
import type { TenantAppConfig } from '@/api/bsl/tenant/app/config/model';
import {
  getAppId,
  getDataScopeId,
  getRoleCode,
  StorageUtil
} from '@/utils/lzd-util';
import tenantDataScopeApi from '@/api/bsl/tenant/data/scope';
import tenantAppConfigApi from '@/api/bsl/tenant/app/config';

/** 直接指定菜单数据 */
const USER_MENUS: Menu[] | null = null;

/** 获取菜单数据并排序 */
const getMenuTree = (data?: Menu[]) => {
  if (!data) {
    return data;
  }
  data = data
    .filter(
      (v) =>
        v.menuType !== 2 &&
        !['内嵌页面', '功能演示', '获取授权', '官网', '文档'].includes(v.title!)
    )
    .sort((a, b) => (a.sortNumber ?? 0) - (b.sortNumber ?? 0));
  data.forEach((v) => (v.children = getMenuTree(v.children || [])));
  return data;
};

export interface UserState {
  info: User | null;
  menus: MenuItem[] | null;
  homePath?: string;
  authorities: (string | undefined)[];
  authoritiesAll: TenantMenu[];
  roles: (string | undefined)[];
  dicts: Record<string, DictionaryData[]>;
  currRole: TenantRole | null;
  currRoles: TenantRole[] | null;
  currDataScope: TenantDataScope | null;
  currDataScopes: TenantDataScope[] | null;
  currAppConfig: TenantAppConfig | null;
  currAppConfigs: TenantAppConfig[];

  coseOtherTab: Function | null;
  push: Function | null;
  fixed: boolean;
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    /** 当前登录用户的信息 */
    info: null,
    /** 当前登录用户的菜单 */
    menus: null,
    homePath: void 0,
    /** 当前登录用户的权限 */
    authorities: [],
    /** 当前登录用户的原始权限 */
    authoritiesAll: [],
    /** 当前登录用户的角色 */
    roles: [],
    /** 字典数据缓存 */
    dicts: {},
    currRole: null,
    currRoles: null,
    currDataScope: null,
    currDataScopes: null,
    currAppConfig: null,
    currAppConfigs: [
      {
        seq: 1,
        appId: 'GP',
        appName: '毕业设计管理'
      }
    ],

    /** 关闭其他标签函数 */
    coseOtherTab: null,
    /** 路由跳转函数 */
    push: null,
    fixed: !(globalThis.innerWidth <= 767 || globalThis.innerHeight <= 500)
  }),
  actions: {
    /**
     * 请求登录用户的个人信息/权限/角色/菜单
     */
    async fetchUserInfo(dataScopeId?: number, callback?: () => void) {
      if (!dataScopeId) {
        dataScopeId = getDataScopeId() ?? '';
        if (dataScopeId) {
          return this.fetchUserInfo(dataScopeId, callback);
        }
      }

      this.currAppConfigs = await tenantAppConfigApi.queryList();
      const resultDataScopes = await tenantDataScopeApi
        .queryList()
        .catch((e) => console.error(e));
      // 设置租户数据范围信息
      if (!resultDataScopes || resultDataScopes.length < 1) {
        return {};
      }
      // 当前学年的判断逻辑<没有存值的时候去判断，即登录时候>
      try {
        if (dataScopeId && +dataScopeId === 20212022) {
          const activeItems = resultDataScopes.filter(
            (item) => item.activated === 1
          );
          if (activeItems && activeItems.length > 0) {
            dataScopeId = activeItems[0].dataScopeId;
          }
        }
      } catch (_err) {}

      // 当前数据范围
      this.currDataScopes = resultDataScopes;
      if (resultDataScopes.length > 0) {
        const indexOf = resultDataScopes.findIndex(
          (d) => d.dataScopeId === dataScopeId
        );
        if (indexOf !== -1) {
          this.currDataScope = resultDataScopes[indexOf];
          StorageUtil.set('currDataScopeId', this.currDataScope.dataScopeId);
        } else {
          const arr = resultDataScopes
            .filter((d) => d.disabled !== 1)
            .sort((a, b) => (b.sortNumber ?? 0) - (a.sortNumber ?? 0));
          if (arr.length > 0) {
            this.currDataScope = arr[0];
            StorageUtil.set('currDataScopeId', this.currDataScope.dataScopeId);
          }
        }
      }
      if (!this.currDataScope) {
        this.currDataScope = resultDataScopes
          .map((d) => d)
          .sort((a, b) => (b.sortNumber ?? 0) - (a.sortNumber ?? 0))[0];
      }

      if (this.currDataScope) {
        dataScopeId = this.currDataScope.dataScopeId;
      }

      const result = await getUserInfo(dataScopeId).catch((e) =>
        console.error(e)
      );
      if (!result) {
        setTimeout(() => callback?.(), 350);
        return {};
      }

      // 获取缓存中的 当前 appId
      let currAppId = getAppId();
      let currAppConfig: TenantAppConfig | null = null;
      if (result.apps && result.apps.length > 0) {
        currAppId = currAppId ?? result.apps[0].k;
        // 只一个应用
        if (result.apps.length === 1) {
          currAppId = result.apps[0].k!;
        }

        currAppConfig = this.currAppConfigs.filter(
          (v) => v.appId === currAppId
        )[0];
      }

      currAppId = currAppId ?? 'GP';
      const filterAppConfigs = this.currAppConfigs.filter(
        (v) => v.appId === currAppId
      );
      currAppConfig =
        filterAppConfigs.length > 0
          ? filterAppConfigs[0]
          : ({
              seq: 1,
              appId: 'GP',
              appName: '毕业设计管理'
            } as TenantAppConfig);

      this.setCurrAppConfigInfo(currAppConfig!);

      // 原始用户角色
      this.currRoles = result.roles ?? [];
      const currRoleSeq = StorageUtil.get('currRoleSeq');
      let arr: TenantRole[] = [];
      if (
        currRoleSeq &&
        (arr = this.currRoles.filter((d) => d?.seq == +currRoleSeq)).length > 0
      ) {
        this.currRole = arr[0];
      } else {
        this.currRole = this.currRoles.length > 0 ? this.currRoles[0] : null;
        StorageUtil.set('currRoleSeq', this.currRole?.seq ?? getDataScopeId());
        StorageUtil.set(
          'currRoleCode',
          this.currRole?.roleCode ?? getRoleCode()
        );
      }

      return await this.fetchUserAuthorities(
        this.currDataScope?.dataScopeId ?? getDataScopeId(),
        this.currRole?.roleCode ?? getRoleCode(),
        callback
      );
    },
    /* 加载当前角色权限 */
    async fetchUserAuthorities(
      dataScopeId: number,
      roleCode: string,
      callback?: () => void
    ) {
      this.roles = [roleCode];

      const __lzd_reload_user_info = () => {
        return getUserInfo(dataScopeId, roleCode);
      };
      // 存入全局缓存, 方便调用
      globalThis.__lzd_reload_user_info = __lzd_reload_user_info;

      const result = await __lzd_reload_user_info();
      this.setInfo(result);
      this.authoritiesAll = result?.authorities?.filter((a) => !!a) ?? [];
      this.authorities =
        this.authoritiesAll.map((d) => d.authority).filter((a) => !!a) ?? [];
      let menuTree = result.authorities?.filter?.((d) => d.menuType !== 2);
      if (!menuTree || menuTree.length === 0) {
        menuTree = [
          {
            title: '未经授权',
            path: '/exception/unauthorized',
            component: '/exception/unauthorized',
            seq: 1,
            parent: 0,
            hide: 1
          } as TenantMenu
        ];
      }
      const { menus, homePath } = formatMenus(
        USER_MENUS ??
          toTree({
            data: getMenuTree(menuTree),
            idField: 'seq',
            parentIdField: 'parent'
          })
      );
      this.setMenus(menus);
      this.homePath = homePath;

      setTimeout(() => callback?.(), 350);
      return { menus, homePath };
    },
    /**
     * 更新用户信息
     */
    setInfo(value: User) {
      this.info = value;
      if (value) {
        StorageUtil.set('currTenantId', value.tenantId);
        if (value.roles) {
          this.currRoles = value.roles;
        }
      }
    },
    /**
     * 更新菜单数据
     */
    setMenus(menus: MenuItem[] | null) {
      this.menus = menus;
    },
    /**
     * 更新菜单的徽章
     * @param path 菜单地址
     * @param value 徽章值
     * @param type 徽章类型
     */
    setMenuBadge(
      path: string,
      value?: number | string | null,
      type?: BadgeProps['type']
    ) {
      this.menus = mapTree(this.menus, (m) => {
        if (path === m.path) {
          const meta = m.meta || {};
          return {
            ...m,
            meta: {
              ...meta,
              props: {
                ...meta.props,
                badge: value == null ? void 0 : { value, type }
              }
            }
          };
        }
        return m;
      });
    },
    /**
     * 更新字典数据
     */
    setDicts(
      value: DictionaryData[] | Record<string, DictionaryData[]>,
      code?: string | null
    ) {
      if (code == null) {
        this.dicts = value as Record<string, DictionaryData[]>;
        return;
      }
      this.dicts[code] = value as DictionaryData[];
    },
    /** 更新应用信息 */
    setCurrAppConfigInfo(value: TenantAppConfig) {
      this.currAppConfig = value;
      StorageUtil.set('currAppId', value.appId ?? 'GP');
      StorageUtil.set('currAppName', value.appName ?? '毕业设计管理');
    },
    /** 切换应用 */
    setCurrAppConfig(value: TenantAppConfig, callback?: () => void) {
      this.setCurrAppConfigInfo(value);
      // 加载用户信息
      this.fetchUserInfo(value.dataScopeId, callback);
    },
    /** 设置学年 */
    setCurrDataScope(value: TenantDataScope, callback?: () => void) {
      this.currDataScope = value;
      this.currRole = null;
      this.currRoles = [];
      this.roles = [];
      this.menus = [];
      this.authorities = [];
      this.authoritiesAll = [];
      StorageUtil.set('currDataScopeId', value.dataScopeId);
      StorageUtil.remove('currRoleSeq');
      StorageUtil.remove('currRoleCode');
      this.fetchUserInfo(value.dataScopeId, callback);
    },
    /** 切换角色 */
    setCurrRole(role: TenantRole, callback?: () => void) {
      EleMessage.success(`已切换为：${role.roleName}`);
      this.currRole = role;
      StorageUtil.set('currRoleSeq', role?.seq);
      StorageUtil.set('currRoleCode', role?.roleCode ?? '');
      // 再次加载数据
      this.fetchUserAuthorities(
        this.currDataScope!.dataScopeId!,
        role.roleCode!,
        callback
      );
    },

    /** 切换当前应用 */
    switchCurrAppConfig(appId: string, callback?: () => void) {
      if (!this.currAppConfigs || this.currAppConfigs.length === 0) {
        return EleMessage.warning(`应用切换错误：${appId}`);
      }
      const arr = this.currAppConfigs.filter((v) => v.appId === appId);
      if (arr.length === 0) {
        return EleMessage.warning(`未授权应用：${appId}`);
      }

      const loading = EleMessage.loading('应用切换中，请稍后...');
      this.setCurrAppConfig(arr[0], () => {
        loading.close();
        setTimeout(() => {
          callback?.();
        }, 350);
      });
    },
    /** 切换当前学年 */
    switchCurrDataScope(dataScopeId: number, callback?: () => void) {
      if (!this.currDataScopes || this.currDataScopes.length === 0) {
        return EleMessage.warning(`学年切换错误：${dataScopeId}`);
      }
      const arr = this.currDataScopes.filter(
        (v) => v.dataScopeId === dataScopeId
      );
      if (arr.length === 0) {
        return EleMessage.warning(`未授权学年：${dataScopeId}`);
      }
      const loading = EleMessage.loading('学年切换中，请稍后...');
      this.setCurrDataScope(arr[0], () => {
        loading.close();
        setTimeout(() => {
          callback?.();
        }, 350);
      });
    },
    /** 切换当前角色 */
    switchCurrRole(roleCode: string, callback?: () => void) {
      if (!this.currRoles || this.currRoles.length === 0) {
        return EleMessage.warning(`角色切换错误：${roleCode}`);
      }
      const arr = this.currRoles.filter((v) => v.roleCode === roleCode);
      if (arr.length === 0) {
        return EleMessage.warning(`未授权角色：${roleCode}`);
      }
      const loading = EleMessage.loading('角色切换中，请稍后...');
      this.setCurrRole(arr[0], () => {
        loading.close();
        setTimeout(() => {
          callback?.();
        }, 350);
      });
    }
  }
});

/**
 * 菜单数据处理为EleProLayout所需要的格式
 * @param data 菜单数据
 * @param childField 子级的字段名称
 */
function formatMenus(data: Menu[], childField = 'children') {
  let homePath: string | undefined;
  let homeTitle: string | undefined;
  const menus = mapTree<Menu, MenuItem>(
    data,
    (item) => {
      const meta: MenuItem['meta'] =
        typeof item.meta === 'string'
          ? JSON.parse(item.meta || '{}')
          : item.meta;
      const menu: MenuItem = {
        path: item.path,
        component: item.component,
        meta: { title: item.title, icon: item.icon, hide: !!item.hide, ...meta }
      };
      const children = item[childField]
        ? item[childField].filter((d: any) => !(d.meta?.hide ?? d.hide))
        : void 0;
      if (!children?.length) {
        if (!homePath && menu.path && !isExternalLink(menu.path)) {
          homePath = menu.path;
          homeTitle = menu.meta?.title;
        }
      } else {
        const childPath = children[0].path;
        if (childPath) {
          if (!menu.redirect) {
            menu.redirect = childPath;
          }
          if (!menu.path) {
            menu.path = childPath.substring(0, childPath.lastIndexOf('/'));
          }
        }
      }
      if (!menu.path) {
        console.error('菜单path不能为空且要唯一:', item);
        return;
      }
      return menu;
    },
    childField
  );
  return { menus, homePath, homeTitle };
}
